import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { api } from '../utils/api';
import { Scan, CheckCircle2, Wifi, WifiOff, X, Download, FileText, Plus, Package } from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocketContext } from '../context/WebSocketContext';
import { handleNumericInputFocus, handleNumericInputMouseUp } from '../utils/inputHelpers';

export const Scanner = () => {
  const [barcode, setBarcode] = useState('');
  const [product, setProduct] = useState(null);
  const [scope, setScope] = useState('warehouse'); // 'warehouse' o 'event'
  const [movementType, setMovementType] = useState('entry');
  const [quantity, setQuantity] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [scanning, setScanning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  
  // Modo lote - ACTIVADO POR DEFECTO
  const [batchMode, setBatchMode] = useState(true);
  const [batchItems, setBatchItems] = useState([]);
  const [confirmingBatch, setConfirmingBatch] = useState(false);
  
  // Albarán generado - para ofrecer descarga
  const [lastDeliveryNoteId, setLastDeliveryNoteId] = useState(null);
  const [lastDeliveryNoteNumber, setLastDeliveryNoteNumber] = useState('');
  const [showDownloadOption, setShowDownloadOption] = useState(false);
  
  // Dialog de nombre de archivo
  const [filenameDialogOpen, setFilenameDialogOpen] = useState(false);
  const [downloadType, setDownloadType] = useState('pdf');
  const [customFilename, setCustomFilename] = useState('');
  const [downloading, setDownloading] = useState(false);
  
  // Dialog de crear producto nuevo
  const [createProductDialogOpen, setCreateProductDialogOpen] = useState(false);
  const [scannedBarcodeForNew, setScannedBarcodeForNew] = useState('');
  const [newProduct, setNewProduct] = useState({
    name: '',
    barcode: '',
    cost: '',
    stock: '',
    has_box: false,
    units_per_box: '',
    box_barcode: ''
  });
  const [creatingProduct, setCreatingProduct] = useState(false);
  
  const inputRef = useRef(null);
  const { isConnected } = useWebSocketContext();

  const fetchActiveEvents = async () => {
    try {
      const response = await api.getActiveEvents();
      setActiveEvents(response.data);
    } catch (error) {
      console.error('Error al cargar eventos activos:', error);
    }
  };

  useEffect(() => {
    if (inputRef.current && !product) {
      inputRef.current.focus();
    }
  }, [product]);

  useEffect(() => {
    if (scope === 'event') {
      fetchActiveEvents();
    }
  }, [scope]);

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!barcode.trim() || scanning) return;
    
    // Validar evento seleccionado en modo evento
    if (scope === 'event' && !selectedEvent) {
      toast.error('Debes seleccionar un evento primero');
      return;
    }
    
    const currentBarcode = barcode.trim();
    setScanning(true);
    setBarcode('');
    
    try {
      const response = await api.getProductByBarcode(currentBarcode);
      const foundProduct = response.data;
      
      if (batchMode) {
        // Esperar un momento antes de procesar para evitar doble submit
        await new Promise(resolve => setTimeout(resolve, 100));
        addToBatch(foundProduct);
        inputRef.current?.focus();
      } else {
        setProduct(foundProduct);
        // Mostrar información de qué se escaneó
        if (foundProduct.scanned_as_box) {
          toast.success(`✓ ${foundProduct.name} 📦 (Caja)`, { duration: 2000 });
        } else if (foundProduct.scanned_as_unit) {
          toast.success(`✓ ${foundProduct.name} (Unidad)`, { duration: 2000 });
        } else {
          toast.success(`✓ ${foundProduct.name}`, { duration: 2000 });
        }
      }
    } catch (error) {
      // Producto no encontrado - ofrecer crear nuevo
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      setScannedBarcodeForNew(currentBarcode);
      setNewProduct({
        name: '',
        barcode: currentBarcode,
        cost: '',
        stock: '0',
        has_box: false,
        units_per_box: '',
        box_barcode: ''
      });
      setCreateProductDialogOpen(true);
    } finally {
      setScanning(false);
      inputRef.current?.focus();
    }
  };

  // Función para crear un nuevo producto desde el escáner
  const handleCreateProduct = async () => {
    if (!newProduct.name.trim()) {
      toast.error('El nombre del producto es obligatorio');
      return;
    }
    if (!newProduct.cost || parseFloat(newProduct.cost) <= 0) {
      toast.error('El precio/coste debe ser mayor a 0');
      return;
    }
    if (newProduct.has_box && (!newProduct.units_per_box || parseInt(newProduct.units_per_box) <= 0)) {
      toast.error('Indica las unidades por caja');
      return;
    }

    setCreatingProduct(true);
    try {
      const initialStock = parseInt(newProduct.stock) || 0;
      const productData = {
        name: newProduct.name.trim(),
        barcode: newProduct.barcode,
        cost: parseFloat(newProduct.cost),
        has_box: newProduct.has_box,
        units_per_box: newProduct.has_box ? parseInt(newProduct.units_per_box) : null,
        box_barcode: newProduct.has_box ? newProduct.box_barcode : null,
        // Usar los campos correctos para stock inicial
        initial_stock: newProduct.has_box ? 0 : initialStock,
        initial_box_stock: 0,
        initial_unit_stock: newProduct.has_box ? initialStock : 0
      };

      const response = await api.createProduct(productData);
      const createdProduct = response.data;
      
      toast.success(`✓ Producto "${createdProduct.name}" creado correctamente`);
      
      // Cerrar dialog y resetear
      setCreateProductDialogOpen(false);
      setNewProduct({
        name: '',
        barcode: '',
        cost: '',
        stock: '0',
        has_box: false,
        units_per_box: '',
        box_barcode: ''
      });
      setScannedBarcodeForNew('');
      
      // Si estamos en modo lote, agregar el producto recién creado al lote
      if (batchMode) {
        addToBatch({ ...createdProduct, scanned_as_box: false });
      } else {
        setProduct(createdProduct);
      }
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al crear el producto';
      toast.error(errorMessage);
    } finally {
      setCreatingProduct(false);
    }
  };

  const addToBatch = (product) => {
    // Determinar si es caja o unidad basado en qué código se escaneó
    const isBox = product.scanned_as_box || false;
    const quantityToAdd = 1; // Siempre 1 (1 caja o 1 unidad según corresponda)
    
    setBatchItems(prevItems => {
      const existingIndex = prevItems.findIndex(item => item.product_id === product.id && item.is_box === isBox);
      
      if (existingIndex >= 0) {
        // Ya existe este tipo (caja o unidad) del producto
        const newItems = [...prevItems];
        const newQuantity = newItems[existingIndex].quantity + quantityToAdd;
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newQuantity
        };
        const typeLabel = isBox ? '📦' : 'ud';
        toast.success(`${product.name}: +${quantityToAdd} ${typeLabel} (Total: ${newQuantity})`, { duration: 1500 });
        return newItems;
      } else {
        // Producto nuevo o tipo diferente
        const typeLabel = isBox ? '📦 caja' : 'unidad';
        toast.success(`✓ ${product.name} (${typeLabel})`, { duration: 1500 });
        return [...prevItems, {
          product_id: product.id,
          barcode: product.barcode,
          name: product.name,
          quantity: quantityToAdd,
          cost: product.cost || 0,
          is_box: isBox
        }];
      }
    });
    
    if (navigator.vibrate) navigator.vibrate(50);
  };

  const updateBatchItemQuantity = (productId, newQuantity, isBox = false) => {
    if (newQuantity < 1) return;
    setBatchItems(prevItems =>
      prevItems.map(item =>
        item.product_id === productId && item.is_box === isBox
          ? { ...item, quantity: parseInt(newQuantity) }
          : item
      )
    );
  };

  const updateBatchItemCost = (productId, newCost, isBox = false) => {
    const costValue = parseFloat(newCost) || 0;
    setBatchItems(prevItems =>
      prevItems.map(item =>
        item.product_id === productId && item.is_box === isBox
          ? { ...item, cost: costValue }
          : item
      )
    );
  };

  const removeBatchItem = (productId, isBox = false) => {
    setBatchItems(prevItems => prevItems.filter(item => 
      !(item.product_id === productId && item.is_box === isBox)
    ));
    toast.info('Producto eliminado del lote');
  };

  const clearBatch = () => {
    setBatchItems([]);
    setBarcode('');
    inputRef.current?.focus();
  };

  const confirmBatch = async () => {
    if (batchItems.length === 0) {
      toast.error('El lote está vacío');
      return;
    }

    if (scope === 'event' && !selectedEvent) {
      toast.error('Debes seleccionar un evento');
      return;
    }

    setConfirmingBatch(true);
    setLastDeliveryNoteId(null);
    setShowDownloadOption(false);
    
    try {
      // FIX: Solo crear el albarán - el backend maneja los movimientos de stock automáticamente
      // Eliminada la llamada redundante a api.createMovement() que causaba duplicación de stock
      let noteType;
      if (scope === 'event') {
        noteType = movementType === 'entry' ? 'return' : 'customer';
      } else {
        noteType = movementType === 'entry' ? 'warehouse_entry' : 'warehouse_exit';
      }
      
      const deliveryNoteData = {
        type: noteType,
        date: new Date().toISOString(),
        customer_id: scope === 'event' ? selectedEvent : null,
        event_id: scope === 'event' ? selectedEvent : null,
        items: batchItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.cost || item.average_cost || 0,
          is_box: item.is_box || false
        })),
        notes: `Generado desde escáner móvil - ${scope === 'event' 
          ? (movementType === 'entry' ? 'Devolución evento' : 'Salida evento')
          : (movementType === 'entry' ? 'Entrada almacén' : 'Salida almacén')}`,
        source: 'scanner'
      };
      
      const response = await api.createDeliveryNote(deliveryNoteData);
      setLastDeliveryNoteId(response.data.id);
      setLastDeliveryNoteNumber(response.data.number);
      setShowDownloadOption(true);
      
      const actionText = scope === 'warehouse'
        ? (movementType === 'entry' ? 'Entradas al almacén' : 'Salidas del almacén')
        : (movementType === 'entry' ? 'Devoluciones de evento' : 'Salidas a evento');
      
      toast.success(`✓ Lote confirmado: ${batchItems.length} productos`, {
        description: actionText,
        duration: 3000
      });
      
      playSuccessSound();
      if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 100]);
      
      // Limpiar lote
      setBatchItems([]);
      setBarcode('');
      inputRef.current?.focus();

    } catch (error) {
      console.error('Error al confirmar lote:', error);
      toast.error(error.response?.data?.detail || 'Error al confirmar lote');
    } finally {
      setConfirmingBatch(false);
    }
  };

  // Generar nombre automático para el archivo
  const generateAutoFilename = () => {
    const typeMap = {
      'entry': scope === 'event' ? 'DEVOLUCION' : 'ENTRADA_ALM',
      'exit': scope === 'event' ? 'SALIDA_EVENTO' : 'SALIDA_ALM'
    };
    const typePrefix = typeMap[movementType] || 'ALBARAN';
    const eventName = selectedEvent && activeEvents.find(e => e.id === selectedEvent)?.name;
    const entityName = eventName 
      ? eventName.replace(/\s+/g, '_').toUpperCase().substring(0, 15) 
      : 'ALMACEN';
    const noteNum = lastDeliveryNoteNumber?.replace('ALB-', '') || 'XXX';
    return `${typePrefix}_${entityName}_${noteNum}`;
  };

  // Abrir dialog para pedir nombre antes de descargar
  const openDownloadDialog = (type) => {
    setDownloadType(type);
    setCustomFilename(generateAutoFilename());
    setFilenameDialogOpen(true);
  };

  // Procesar la descarga con el nombre personalizado
  const handleDownloadWithName = async () => {
    if (!lastDeliveryNoteId || !customFilename.trim()) {
      toast.error('Debes indicar un nombre para el documento');
      return;
    }

    setDownloading(true);
    try {
      // Primero guardar el nombre personalizado en la base de datos
      await api.updateDeliveryNoteFilename(lastDeliveryNoteId, customFilename.trim());
      
      // Luego descargar el archivo
      if (downloadType === 'pdf') {
        const response = await api.getDeliveryNotePDF(lastDeliveryNoteId);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${customFilename.trim()}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF descargado');
      } else {
        const response = await api.getDeliveryNoteExcel(lastDeliveryNoteId);
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${customFilename.trim()}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('Excel descargado');
      }
      
      setFilenameDialogOpen(false);
      setCustomFilename('');
    } catch (error) {
      toast.error('Error al descargar el documento');
    } finally {
      setDownloading(false);
    }
  };

  const handleMovementSubmit = async (e) => {
    e.preventDefault();
    if (!product || !quantity) return;
    
    // Validar que si el scope es 'event', se haya seleccionado un evento
    if (scope === 'event' && !selectedEvent) {
      toast.error('Debes seleccionar un evento');
      return;
    }

    setLastDeliveryNoteId(null);
    setShowDownloadOption(false);

    try {
      // Determinar tipo de albarán según scope y movementType
      let noteType;
      if (scope === 'event') {
        noteType = movementType === 'entry' ? 'return' : 'customer';
      } else {
        noteType = movementType === 'entry' ? 'warehouse_entry' : 'warehouse_exit';
      }
      
      // El albarán maneja todo: crea el movimiento de stock automáticamente
      const deliveryNoteData = {
        type: noteType,
        date: new Date().toISOString(),
        customer_id: scope === 'event' ? selectedEvent : null,
        event_id: scope === 'event' ? selectedEvent : null,
        items: [{
          product_id: product.id,
          quantity: parseInt(quantity),
          price: movementType === 'entry' && cost ? parseFloat(cost) : (product.average_cost || product.cost || 0),
          is_box: product.scanned_as_box || false
        }],
        notes: notes || `Generado desde escáner móvil - ${scope === 'event' 
          ? (movementType === 'entry' ? 'Devolución evento' : 'Salida evento')
          : (movementType === 'entry' ? 'Entrada almacén' : 'Salida almacén')}`,
        source: 'scanner'
      };
      
      const noteResponse = await api.createDeliveryNote(deliveryNoteData);
      setLastDeliveryNoteId(noteResponse.data.id);
      setLastDeliveryNoteNumber(noteResponse.data.number);
      setShowDownloadOption(true);
      console.log('📄 Albarán generado:', noteResponse.data.number);
      
      const typeLabel = product.scanned_as_box ? '📦 caja(s)' : 'unidad(es)';
      const actionText = scope === 'warehouse' 
        ? (movementType === 'entry' ? 'Entrada al almacén' : 'Salida del almacén')
        : (movementType === 'entry' ? 'Devolución de evento' : 'Salida a evento');
      
      // Mostrar toast de éxito
      toast.success(`✓ ${actionText} registrada`, {
        description: `${product.name} - ${quantity} ${typeLabel} (Albarán generado)`,
        duration: 2000
      });
      
      // Efectos visuales y sonoros
      setShowSuccess(true);
      playSuccessSound();
      if (navigator.vibrate) navigator.vibrate(200);
      
      // Limpiar formulario después de 1.5 segundos
      setTimeout(() => {
        console.log('🧹 Limpiando formulario...');
        setShowSuccess(false);
        setBarcode('');
        setProduct(null);
        setQuantity('');
        setCost('');
        setNotes('');
        if (scope === 'warehouse') {
          setSelectedEvent('');
        }
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }, 1500);
      
    } catch (error) {
      playErrorSound();
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const playSuccessSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const playErrorSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const resetScanner = () => {
    setBarcode('');
    setProduct(null);
    setQuantity('');
    setCost('');
    setNotes('');
    setSelectedEvent('');
    inputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Success Overlay */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-2xl animate-in zoom-in duration-300 max-w-xs mx-4">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">¡Registrado!</h2>
              <p className="text-slate-600 dark:text-slate-400">
                {movementType === 'entry' ? 'Entrada' : 'Salida'} confirmada
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scan className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Escáner</h1>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
            isConnected 
              ? 'bg-green-100 text-green-700' 
              : 'bg-red-100 text-red-700'
          }`}>
            {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {isConnected ? 'Online' : 'Offline'}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Selector de Ámbito: Almacén General / Evento */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-2 shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setScope('warehouse');
                setSelectedEvent('');
              }}
              className={`
                py-3 px-3 rounded-xl font-semibold text-sm transition-all duration-200
                ${scope === 'warehouse'
                  ? 'bg-blue-600 text-white shadow-md scale-[1.02]'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }
              `}
            >
              🏪 Almacén General
            </button>
            <button
              type="button"
              onClick={() => setScope('event')}
              className={`
                py-3 px-3 rounded-xl font-semibold text-sm transition-all duration-200
                ${scope === 'event'
                  ? 'bg-purple-600 text-white shadow-md scale-[1.02]'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }
              `}
            >
              🎪 Evento
            </button>
          </div>
        </div>

        {/* Selector de Evento (solo si scope === 'event') */}
        {scope === 'event' && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200 dark:border-slate-800">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              Selecciona un Evento
            </label>
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="w-full h-12 px-4 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-medium"
              required={scope === 'event'}
            >
              <option value="">-- Selecciona un evento --</option>
              {activeEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Lista de items del lote (solo si batchMode) */}
        {batchMode && batchItems.length > 0 && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-amber-200 dark:border-amber-800">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm sm:text-base">
                📦 Lote Actual ({batchItems.length} productos)
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearBatch}
                className="text-xs w-full sm:w-auto"
              >
                Limpiar
              </Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {batchItems.map((item, index) => (
                <div key={`${item.product_id}-${item.is_box}-${index}`} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                      {item.name}
                      {item.is_box && <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded">📦 Caja</span>}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{item.barcode}</p>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    {/* Campo de precio - solo visible en ENTRADA */}
                    {movementType === 'entry' && (
                      <div className="relative flex-1 sm:w-24">
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={item.cost || ''}
                          onChange={(e) => updateBatchItemCost(item.product_id, e.target.value, item.is_box)}
                          onFocus={handleNumericInputFocus}
                          onMouseUp={handleNumericInputMouseUp}
                          placeholder="0.00"
                          className="h-10 text-center font-medium pr-6"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                      </div>
                    )}
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateBatchItemQuantity(item.product_id, e.target.value, item.is_box)}
                      onFocus={handleNumericInputFocus}
                      onMouseUp={handleNumericInputMouseUp}
                      className="flex-1 sm:w-20 h-10 text-center font-bold"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBatchItem(item.product_id, item.is_box)}
                      className="h-10 w-10 p-0 shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              <Button
                type="button"
                onClick={confirmBatch}
                disabled={confirmingBatch || batchItems.length === 0 || (scope === 'event' && !selectedEvent)}
                className="w-full h-14 text-base sm:text-lg font-bold bg-amber-600 hover:bg-amber-700"
              >
                {confirmingBatch ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Confirmando...
                  </div>
                ) : (
                  `Confirmar Lote (${batchItems.length} productos)`
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Panel de descarga de albarán */}
        {showDownloadOption && lastDeliveryNoteId && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-4 shadow-sm border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-800 dark:text-green-200">
                  ✓ Albarán generado
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  Puedes descargarlo ahora o encontrarlo en Albaranes
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                onClick={() => openDownloadDialog('pdf')}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button
                type="button"
                onClick={() => openDownloadDialog('excel')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                Excel
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowDownloadOption(false)}
              className="w-full mt-2 text-xs text-green-600"
            >
              Cerrar
            </Button>
          </div>
        )}

        {/* Dialog para nombre del archivo */}
        <Dialog open={filenameDialogOpen} onOpenChange={setFilenameDialogOpen}>
          <DialogContent className="sm:max-w-md mx-4">
            <DialogHeader>
              <DialogTitle>
                {downloadType === 'pdf' ? '📄 Descargar PDF' : '📊 Descargar Excel'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  Indica el nombre del documento. Se guardará para futuras descargas.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="scanner-filename">Nombre del documento</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="scanner-filename"
                    value={customFilename}
                    onChange={(e) => setCustomFilename(e.target.value)}
                    placeholder="Ej: ALBARAN_FERIA_MAYO"
                    className="flex-1"
                  />
                  <span className="text-sm text-slate-500">
                    .{downloadType === 'pdf' ? 'pdf' : 'xlsx'}
                  </span>
                </div>
              </div>
              
              {lastDeliveryNoteNumber && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="font-medium">{lastDeliveryNoteNumber}</p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFilenameDialogOpen(false);
                    setCustomFilename('');
                  }}
                  disabled={downloading}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDownloadWithName}
                  disabled={downloading || !customFilename.trim()}
                  className={`flex-1 ${downloadType === 'pdf' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {downloading ? 'Descargando...' : 'Descargar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog para crear producto nuevo */}
        <Dialog open={createProductDialogOpen} onOpenChange={setCreateProductDialogOpen}>
          <DialogContent className="sm:max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Crear Producto Nuevo
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-700">
                  El código <span className="font-mono font-bold">{scannedBarcodeForNew}</span> no está registrado. 
                  Puedes crear un producto nuevo con este código.
                </p>
              </div>
              
              {/* Nombre */}
              <div className="space-y-2">
                <Label htmlFor="new-product-name">Nombre del producto *</Label>
                <Input
                  id="new-product-name"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  placeholder="Ej: Coca Cola 2L"
                  autoFocus
                />
              </div>
              
              {/* Código de barras (readonly) */}
              <div className="space-y-2">
                <Label htmlFor="new-product-barcode">Código de barras</Label>
                <Input
                  id="new-product-barcode"
                  value={newProduct.barcode}
                  readOnly
                  className="bg-slate-100 font-mono"
                />
              </div>
              
              {/* Precio/Coste */}
              <div className="space-y-2">
                <Label htmlFor="new-product-cost">Precio/Coste (€) *</Label>
                <Input
                  id="new-product-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newProduct.cost}
                  onChange={(e) => setNewProduct({...newProduct, cost: e.target.value})}
                  placeholder="0.00"
                  onFocus={handleNumericInputFocus}
                  onMouseUp={handleNumericInputMouseUp}
                />
              </div>
              
              {/* Stock inicial */}
              <div className="space-y-2">
                <Label htmlFor="new-product-stock">Stock inicial</Label>
                <Input
                  id="new-product-stock"
                  type="number"
                  min="0"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})}
                  placeholder="0"
                  onFocus={handleNumericInputFocus}
                  onMouseUp={handleNumericInputMouseUp}
                />
              </div>
              
              {/* Toggle de cajas */}
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">¿Se vende en cajas?</p>
                    <p className="text-xs text-slate-500">Activa si el producto viene en cajas con unidades</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewProduct({...newProduct, has_box: !newProduct.has_box})}
                    className={`
                      relative w-12 h-6 rounded-full transition-colors duration-200
                      ${newProduct.has_box ? 'bg-blue-600' : 'bg-slate-300'}
                    `}
                  >
                    <span
                      className={`
                        absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                        ${newProduct.has_box ? 'translate-x-6' : ''}
                      `}
                    />
                  </button>
                </div>
                
                {newProduct.has_box && (
                  <div className="mt-4 space-y-3 pt-3 border-t border-slate-200">
                    <div className="space-y-2">
                      <Label htmlFor="new-units-per-box">Unidades por caja *</Label>
                      <Input
                        id="new-units-per-box"
                        type="number"
                        min="1"
                        value={newProduct.units_per_box}
                        onChange={(e) => setNewProduct({...newProduct, units_per_box: e.target.value})}
                        placeholder="12"
                        onFocus={handleNumericInputFocus}
                        onMouseUp={handleNumericInputMouseUp}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-box-barcode">Código de barras de la caja (opcional)</Label>
                      <Input
                        id="new-box-barcode"
                        value={newProduct.box_barcode}
                        onChange={(e) => setNewProduct({...newProduct, box_barcode: e.target.value})}
                        placeholder="Escanea o escribe el código"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Botones */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setCreateProductDialogOpen(false);
                    setNewProduct({
                      name: '',
                      barcode: '',
                      cost: '',
                      stock: '0',
                      has_box: false,
                      units_per_box: '',
                      box_barcode: ''
                    });
                  }}
                  disabled={creatingProduct}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateProduct}
                  disabled={creatingProduct || !newProduct.name.trim() || !newProduct.cost}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {creatingProduct ? 'Creando...' : 'Crear Producto'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Tabs Entrada/Salida */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-2 shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMovementType('entry')}
              className={`
                py-4 px-4 rounded-xl font-semibold text-base transition-all duration-200
                ${movementType === 'entry'
                  ? 'bg-green-600 text-white shadow-md scale-[1.02]'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }
              `}
            >
              ENTRADA
            </button>
            <button
              type="button"
              onClick={() => setMovementType('exit')}
              className={`
                py-4 px-4 rounded-xl font-semibold text-base transition-all duration-200
                ${movementType === 'exit'
                  ? 'bg-orange-600 text-white shadow-md scale-[1.02]'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }
              `}
            >
              SALIDA
            </button>
          </div>
        </div>

        {/* Toggle Modo Lote */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                batchMode ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-slate-100 dark:bg-slate-800'
              }`}>
                <span className="text-xl">{batchMode ? '📦' : '📱'}</span>
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm sm:text-base">
                  {batchMode ? 'Modo Lote Activo' : 'Escaneo Individual'}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {batchMode ? 'Escanea múltiples productos' : 'Registra uno por uno'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const newMode = !batchMode;
                setBatchMode(newMode);
                // Al cambiar de modo, limpiar estado
                if (newMode) {
                  // Cambio a modo lote: limpiar producto individual
                  setProduct(null);
                  setQuantity('');
                } else {
                  // Cambio a modo individual: limpiar lote
                  setBatchItems([]);
                }
                setBarcode('');
                setTimeout(() => inputRef.current?.focus(), 100);
              }}
              aria-label={batchMode ? 'Desactivar modo lote' : 'Activar modo lote'}
              className={`
                relative inline-flex h-10 w-16 sm:h-8 sm:w-14 items-center rounded-full transition-colors touch-manipulation cursor-pointer
                ${batchMode ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-600'}
              `}
            >
              <span
                className={`
                  inline-block h-8 w-8 sm:h-6 sm:w-6 transform rounded-full bg-white transition-transform shadow-md
                  ${batchMode ? 'translate-x-7 sm:translate-x-7' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>

        {/* ================= MODO LOTE ================= */}
        {batchMode ? (
          <div className="space-y-4">
            {/* Campo de escaneo en modo lote */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200 dark:border-slate-800">
              <form onSubmit={handleBarcodeSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Código de Barras
                  </label>
                  <Input
                    ref={inputRef}
                    type="text"
                    inputMode="text"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    placeholder="Escanea productos para el lote"
                    className="w-full h-14 text-base sm:text-lg font-mono border-2 border-amber-300 dark:border-amber-600 rounded-xl px-4"
                    autoFocus
                  />
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    📦 Modo Lote: Escanea múltiples productos
                  </p>
                </div>
                <Button 
                  type="submit" 
                  variant="default" 
                  className="w-full h-12 sm:h-14 text-base sm:text-lg font-bold"
                  disabled={!barcode || scanning || (scope === 'event' && !selectedEvent)}
                >
                  {scanning ? 'Buscando...' : 'Agregar al Lote'}
                </Button>
              </form>
            </div>
          </div>
        ) : (
          /* ================= MODO INDIVIDUAL ================= */
          !product ? (
          <div className="space-y-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200 dark:border-slate-800">
              <form onSubmit={handleBarcodeSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Código de Barras
                  </label>
                  <Input
                    ref={inputRef}
                    type="text"
                    inputMode="text"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    placeholder="Escanea o escribe el código"
                    className="w-full h-14 text-base sm:text-lg font-mono border-2 border-slate-300 dark:border-slate-600 rounded-xl px-4"
                    autoFocus
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={!barcode || scanning || (scope === 'event' && !selectedEvent)}
                  className="w-full h-14 text-base sm:text-lg font-semibold bg-blue-600 hover:bg-blue-700 rounded-xl"
                >
                  {scanning ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Buscando...
                    </div>
                  ) : (
                    <>
                      <Scan className="h-5 w-5 mr-2" />
                      Buscar Producto
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Info Card */}
            <div className={`rounded-2xl p-4 border ${
              scope === 'warehouse' 
                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                : 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
            }`}>
              <p className={`text-sm text-center font-medium ${
                scope === 'warehouse'
                  ? 'text-blue-900 dark:text-blue-100'
                  : 'text-purple-900 dark:text-purple-100'
              }`}>
                {scope === 'warehouse' 
                  ? '🏪 Modo: Almacén General - Escanea el código de barras'
                  : `🎪 Modo: Evento${selectedEvent ? ' seleccionado' : ' - Selecciona un evento arriba'}`
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Product Info */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border-2 border-blue-200 dark:border-blue-800">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    {product.name}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">
                    {product.barcode}
                  </p>
                  {/* Indicador de tipo escaneado */}
                  {product.has_box && (
                    <div className="mt-2">
                      {product.scanned_as_box ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          📦 Escaneado como CAJA
                        </span>
                      ) : product.scanned_as_unit ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                          🔢 Escaneado como UNIDAD
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
                <button
                  onClick={resetScanner}
                  className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Stock Info - diferenciado para productos con caja */}
              {product.has_box && product.stock_info ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">📦 Cajas</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{product.stock_info.box_stock}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">🔢 Sueltas</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{product.stock_info.unit_stock}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total</p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{product.stock_info.total_units}</p>
                    </div>
                  </div>
                  <p className="text-xs text-center text-slate-500 dark:text-slate-400">
                    {product.units_per_box} unidades por caja
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Stock Actual</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{product.stock}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Coste</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">€{(product.cost || 0).toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Movement Form */}
            <form onSubmit={handleMovementSubmit} className="space-y-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {product.has_box 
                      ? (product.scanned_as_box ? 'Cantidad de CAJAS *' : 'Cantidad de UNIDADES *')
                      : 'Cantidad *'
                    }
                  </label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    onFocus={handleNumericInputFocus}
                    onMouseUp={handleNumericInputMouseUp}
                    placeholder="0"
                    className={`h-14 text-xl font-bold border-2 rounded-xl px-4 ${
                      product.has_box && product.scanned_as_box 
                        ? 'border-blue-300 dark:border-blue-600' 
                        : product.has_box && product.scanned_as_unit
                          ? 'border-green-300 dark:border-green-600'
                          : ''
                    }`}
                    min="1"
                    required
                  />
                  {product.has_box && (
                    <p className={`text-xs mt-1 ${
                      product.scanned_as_box 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-green-600 dark:text-green-400'
                    }`}>
                      {product.scanned_as_box 
                        ? `📦 Registrando entrada/salida de CAJAS (${product.units_per_box} uds/caja)`
                        : '🔢 Registrando entrada/salida de UNIDADES sueltas'
                      }
                    </p>
                  )}
                </div>

                {/* Campo de Coste solo para entradas de almacén general (no para devoluciones) */}
                {movementType === 'entry' && scope === 'warehouse' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      {product.has_box && product.scanned_as_box 
                        ? 'Coste por CAJA (opcional)'
                        : 'Coste por Unidad (opcional)'
                      }
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                      placeholder="0.00"
                      className="h-14 text-lg font-mono border-2 rounded-xl px-4"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Actualiza el último coste y coste medio del producto
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Notas (opcional)
                  </label>
                  <Input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Observaciones..."
                    className="h-12 border-2 rounded-xl"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={!quantity || (scope === 'event' && !selectedEvent)}
                className={`
                  w-full h-16 text-xl font-bold rounded-2xl shadow-lg
                  ${movementType === 'entry'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-orange-600 hover:bg-orange-700'
                  }
                `}
              >
                {scope === 'warehouse' 
                  ? (movementType === 'entry' ? 'Registrar Entrada al Almacén' : 'Registrar Salida del Almacén')
                  : (movementType === 'entry' ? 'Registrar Devolución al Evento' : 'Registrar Salida al Evento')
                }
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={resetScanner}
                className="w-full h-14 text-base font-semibold rounded-2xl border-2"
              >
                Cancelar
              </Button>
            </form>
          </div>
        )
        )}
      </div>
    </div>
  );
};
